/* eslint-disable react-hooks/exhaustive-deps */
import {
  Badge,
  Button,
  Checkbox,
  Collapse,
  Flex,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { IconChevronDown, IconChevronRight, IconSparkles } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useWorkspaceSkills } from "../../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import notify from "../../../../../utils/notify";

type Props = {
  onCompleteAction: (action: any, type: string) => void;
  opened: boolean;
  close: () => void;
  data?: any;
};

const RunSkillsModal = ({
  onCompleteAction, opened, close, data,
}: Props) => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>(data?.skill_ids || []);
  const [mode, setMode] = useState<string>(data?.skill_execution_mode || "per_entity");
  const [maxEntities, setMaxEntities] = useState<number>(data?.skill_max_entities || 50);

  const { data: skills, isLoading }: any = useWorkspaceSkills({
    workspaceId: workspace?.id,
    enabled: opened && !!workspace?.id,
  });

  useEffect(() => {
    if (data) {
      setSelectedSkillIds(data.skill_ids || []);
      setMode(data.skill_execution_mode || "per_entity");
      setMaxEntities(data.skill_max_entities || 50);
    }
  }, [data]);

  const enabledSkills = (skills || []).filter((s: any) => s.is_enabled && s.visibility !== "protected");
  const [expandedOutputSkill, setExpandedOutputSkill] = useState<string | null>(null);

  const selectedSkillsWithOutput = useMemo(
    () => enabledSkills.filter((s: any) => selectedSkillIds.includes(s.id) && s.structured_output?.length > 0),
    [enabledSkills, selectedSkillIds],
  );

  return (
    <Modal
      padding="lg"
      classNames={{
        title: "text-gray-900 text-xl font-bold",
      }}
      size={700}
      opened={opened}
      centered
      onClose={close}
      title={(
        <Flex align="center">
          <IconSparkles className="w-6 h-6 mr-2 text-gray-600" />
          <Stack gap={0}>
            <Text>Run AI Skills</Text>
            <Text size="xs" c="text.5" fw={500}>
              Run AI skills to generate analysis documents for matched entities.
            </Text>
          </Stack>
        </Flex>
      )}
    >
      {isLoading ? (
        <Flex justify="center" py="xl">
          <Loader size="sm" />
        </Flex>
      ) : (
        <Stack gap="md">
          <div>
            <Text size="sm" fw={600} c="text.7" mb={4}>Select Skills</Text>
            {enabledSkills.length === 0 ? (
              <Text size="sm" c="text.5">No enabled skills found. Create skills in Settings first.</Text>
            ) : (
              <Checkbox.Group value={selectedSkillIds} onChange={setSelectedSkillIds}>
                <Stack gap="xs">
                  {enabledSkills.map((skill: any) => {
                    const hasOutput = skill.structured_output?.length > 0;
                    const isSelected = selectedSkillIds.includes(skill.id);
                    const isExpanded = expandedOutputSkill === skill.id;
                    return (
                      <div key={skill.id}>
                        <Checkbox
                          value={skill.id}
                          label={(
                            <Flex align="center" gap="xs">
                              <div>
                                <Flex align="center" gap={6}>
                                  <Text size="sm" fw={500}>{skill.display_name}</Text>
                                  {hasOutput && (
                                    <Badge size="xs" variant="light" color="violet">
                                      {skill.structured_output.length} output fields
                                    </Badge>
                                  )}
                                </Flex>
                                {skill.description && (
                                  <Text size="xs" c="text.5">{skill.description}</Text>
                                )}
                              </div>
                            </Flex>
                          )}
                        />
                        {hasOutput && isSelected && (
                          <div className="ml-7 mt-1">
                            <button
                              type="button"
                              onClick={() => setExpandedOutputSkill(isExpanded ? null : skill.id)}
                              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                            >
                              {isExpanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
                              Output format
                            </button>
                            <Collapse in={isExpanded}>
                              <div className="mt-1 rounded border border-gray-200 bg-gray-50 p-2">
                                <Stack gap={4}>
                                  {skill.structured_output.map((field: any, i: number) => (
                                    <Flex key={i} align="center" gap="xs">
                                      <Text size="xs" fw={500} c="text.7">{field.fieldName}</Text>
                                      <Badge size="xs" variant="outline" color="gray">{field.fieldType}</Badge>
                                      {field.required && <Badge size="xs" variant="light" color="red">required</Badge>}
                                      {field.description && (
                                        <Text size="xs" c="text.5" className="ml-auto">{field.description}</Text>
                                      )}
                                    </Flex>
                                  ))}
                                </Stack>
                                <Text size="xs" c="text.4" mt={6}>
                                  Edit output format in the skill editor.
                                </Text>
                              </div>
                            </Collapse>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Stack>
              </Checkbox.Group>
            )}
          </div>

          <Select
            label="Execution Mode"
            size="sm"
            value={mode}
            onChange={(val) => setMode(val || "per_entity")}
            data={[
              { value: "per_entity", label: "Per Entity" },
              { value: "batch_summary", label: "Batch Summary" },
            ]}
            classNames={{ label: "text-gray-500 font-bold mb-1" }}
          />

          <NumberInput
            label="Max Entities"
            size="sm"
            value={maxEntities}
            onChange={(val) => setMaxEntities(Number(val) || 50)}
            min={1}
            max={500}
            classNames={{ label: "text-gray-500 font-bold mb-1" }}
          />

          <Flex justify="flex-end" gap={5} className="mt-2">
            <Button
              onClick={close}
              variant="outline"
              className="hover:bg-red-100"
              color="red"
            >
              Cancel
            </Button>
            <Button
              disabled={selectedSkillIds.length === 0}
              onClick={() => {
                onCompleteAction({
                  type: "run_skills",
                  skill_ids: selectedSkillIds,
                  skill_execution_mode: mode,
                  skill_max_entities: maxEntities,
                }, "run_skills");
                notify({
                  messageList: ["Run Skills action added successfully"],
                  type: "info",
                });
                close();
              }}
            >
              Save Action
            </Button>
          </Flex>
        </Stack>
      )}
    </Modal>
  );
};

export default RunSkillsModal;
